import { FC } from "react"
import { observer } from "mobx-react"
import { AutoComplete, Col, DatePicker, Form, Row, Select } from "antd"
import moment, { Moment } from "moment"
import { useStore } from "stores/utils/use-store"
import { Entitlement, EntitlementFilter } from "../store/types"

/**
 * One entitlement row as the operator edits it. Shared by the create-contract
 * modal and the settings editor so the two screens cannot drift into offering
 * different axes for the same allow-list.
 *
 * `dateFrom` is a Moment here and an ISO string on the wire — antd v4's
 * DatePicker only speaks moment.
 */
export interface EntitlementFormValues {
  gameIds?: number[]
  regions?: string[]
  tags?: string[]
  utmSource?: string
  dateFrom?: Moment | null
}

export function entitlementToForm(e: Entitlement): EntitlementFormValues {
  return {
    gameIds: e.gameIds ?? [],
    regions: e.regions ?? [],
    tags: e.tags ?? [],
    utmSource: e.utmSource ?? undefined,
    dateFrom: e.dateFrom ? moment(e.dateFrom) : null,
  }
}

// An empty axis means "any" for that axis. Send it absent rather than as an
// empty array so a row written from either screen serializes identically.
export function formToEntitlement(v: EntitlementFormValues): EntitlementFilter {
  // The games Select is fed numeric option values, but a stale form value
  // rehydrated from a tag-mode edit can arrive as a string — coerce and drop
  // anything that is not a real id rather than sending NaN to the DTO.
  const gameIds = (v.gameIds ?? []).map(Number).filter(n => !Number.isNaN(n))

  return {
    gameIds: gameIds.length ? gameIds : undefined,
    regions: v.regions?.length ? v.regions : undefined,
    tags: v.tags?.length ? v.tags : undefined,
    utmSource: v.utmSource || undefined,
    dateFrom: v.dateFrom ? v.dateFrom.toISOString() : undefined,
  }
}

// A row with every axis unset matches everything — the ledger's "unrestricted"
// entitlement. Distinct from having NO rows at all, which matches nothing.
export function isUnrestricted(f: EntitlementFilter): boolean {
  return Object.values(f).every(x => x === undefined)
}

export function describeEntitlement(f: EntitlementFilter): string {
  if (isUnrestricted(f)) return "any game, any country, any tag, any source, any date"
  const parts: string[] = []
  if (f.gameIds?.length) parts.push(`${f.gameIds.length} game(s)`)
  if (f.regions?.length) parts.push(f.regions.join("/"))
  if (f.tags?.length) parts.push(`tags ${f.tags.join("/")}`)
  if (f.utmSource) parts.push(`utm ${f.utmSource}`)
  if (f.dateFrom) parts.push(`from ${f.dateFrom.slice(0, 10)}`)

  return parts.join(" · ")
}

interface EntitlementFieldsProps {
  // Form.List item prefix, e.g. [3]. Empty for a flat single-row form.
  prefix?: Array<string | number>
  // Form.List rows sit inside an already-tight card; drop the bottom margin.
  compact?: boolean
}

export const EntitlementFields: FC<EntitlementFieldsProps> = observer(({ prefix = [], compact = false }) => {
  const { dataStore: { dataDeliveryStore: store } } = useStore()
  const itemStyle = { marginBottom: compact ? 0 : 8 }

  // Fixed 2-col grid: each field keeps its column, so a growing Games select
  // wraps its tags vertically instead of pushing Countries onto a new line.
  return (
    <Row gutter={[16, 8]}>
      <Col span={12}>
        <Form.Item name={[...prefix, "gameIds"]} label="Games" style={itemStyle}>
          <Select
            mode="multiple"
            showSearch
            optionFilterProp="label"
            style={{ width: "100%" }}
            placeholder="any game"
            options={(store.games ?? []).map(g => ({ value: g.gameId, label: `${g.gameId} · ${g.gameName}` }))}
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item name={[...prefix, "regions"]} label="Countries" style={itemStyle}>
          <Select
            mode="tags"
            style={{ width: "100%" }}
            placeholder="any country"
            tokenSeparators={[","]}
            options={(store.facets.regions ?? []).map(r => ({ value: r, label: r }))}
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item name={[...prefix, "tags"]} label="Tags" style={itemStyle}>
          <Select
            mode="tags"
            style={{ width: "100%" }}
            placeholder="any tag"
            tokenSeparators={[","]}
            options={(store.facets.tags ?? []).map(t => ({ value: t, label: t }))}
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item name={[...prefix, "utmSource"]} label="UTM source" style={itemStyle}>
          <AutoComplete
            allowClear
            style={{ width: "100%" }}
            placeholder="any"
            options={(store.facets.utmSources ?? []).map(u => ({ value: u }))}
            filterOption={(input, opt) => String(opt?.value ?? "").toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item name={[...prefix, "dateFrom"]} label="Start date" style={itemStyle}>
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>
      </Col>
    </Row>
  )
})
